import {
  addHours,
  parseISO,
  isBefore,
  isAfter,
  startOfDay,
  endOfDay,
} from "date-fns";
import { utcToZonedTime, getTimezoneOffset } from "date-fns-tz";

type GetMaxDateParams = {
  maximumAdvanceBookingHours: number;
  endDate?: string | null;
  medspaTimezone?: string;
};

type GetMinDateParams = {
  startDate?: string | null;
  medspaTimezone?: string;
};

/**
 * Gets the current time in the medspa's timezone
 */
export const getCurrentTimeInTimezone = (timezone?: string): Date => {
  const now = new Date();

  return timezone ? utcToZonedTime(now, timezone) : now;
};

const addTimezoneOffset = (date: Date, medspaTimezone?: string) => {
  if (!medspaTimezone) return date;

  const localTimeTZOffset = (new Date().getTimezoneOffset() / 60) * -1;

  const medspaTZOffset =
    -1 *
    Math.floor((getTimezoneOffset(medspaTimezone) / (1000 * 60 * 60)) % 24);

  // Calculate offset to add/substract to difference in hours depending on the
  // local machine timezone offset and the medspa timezone offset
  const offset = medspaTZOffset + localTimeTZOffset;

  return addHours(date, offset);
};

export const getMedspaStartOfDay = (date?: Date, medspaTimezone?: string) =>
  addTimezoneOffset(startOfDay(date ?? new Date()), medspaTimezone);

export const getMedspaEndOfDay = (date?: Date, medspaTimezone?: string) =>
  addTimezoneOffset(endOfDay(date ?? new Date()), medspaTimezone);

/**
/**
 * Determines the latest possible date for a booking based on configured constraints.
 * @param maximumAdvanceBookingHours - The maximum number of hours into the future that a booking can be made
 *                                    (e.g., if set to 168, bookings can only be made up to 7 days in advance)
 * @param endDate - Optional end date for selective booking links
 * @param medspaTimezone - The timezone of the medspa
 * @returns The latest possible date for booking
 */
export const getLatestBookingDate = ({
  maximumAdvanceBookingHours,
  endDate,
  medspaTimezone,
}: GetMaxDateParams): Date => {
  const currentDate = getCurrentTimeInTimezone(medspaTimezone);
  const maxDateFromHours = addHours(currentDate, maximumAdvanceBookingHours);

  if (!endDate) {
    return maxDateFromHours;
  }

  // Parse the ISO string and convert to the medspa's timezone if specified
  const endDateTime = medspaTimezone
    ? utcToZonedTime(parseISO(endDate), medspaTimezone)
    : parseISO(endDate);

  // If end date is before max date from hours
  if (isBefore(endDateTime, maxDateFromHours)) {
    // If end date is before current date, use current date
    if (isBefore(endDateTime, currentDate)) {
      return currentDate;
    }
    // Otherwise use end date
    return endDateTime;
  }

  // If end date is after max date from hours, use max date
  return maxDateFromHours;
};

/**
 * Determines the earliest possible date for a booking.
 * @param startDate - Optional start date for selective booking links
 * @param medspaTimezone - The timezone of the medspa
 * @returns The earliest possible date for booking
 */
export const getEarliestBookingDate = ({
  startDate,
  medspaTimezone,
}: GetMinDateParams): Date => {
  const currentDate = getCurrentTimeInTimezone(medspaTimezone);

  if (!startDate) {
    return currentDate;
  }

  // Parse the ISO string and convert to the medspa's timezone if specified
  const startDateTime = medspaTimezone
    ? utcToZonedTime(parseISO(startDate), medspaTimezone)
    : parseISO(startDate);

  // If startDate is in the future, use it as the earliest date
  // Otherwise use current date

  return isAfter(startDateTime, currentDate) ? startDateTime : currentDate;
};
